import React, { useState, useEffect, useCallback } from 'react';
import { X, Printer } from 'lucide-react';

// Printable grades bulletin: per subject — each exam's score, then the
// period average EMPHASIZED (Exam 1 · Exam 2 · ▌Trimester 1▐), a General
// column, competency checkmarks under their subjects, moyenne générale.
const GradesReportModal = ({ apiCall, year, student, onClose, showError }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      setData(await apiCall(`/academic/reports/grades-card?studentId=${encodeURIComponent(student.StudentID)}&academicYear=${year}`));
    } catch (err) { showError(err.message); }
  }, [apiCall, student.StudentID, year, showError]);

  useEffect(() => { load(); }, [load]);

  const buildHTML = () => {
    if (!data) return '';
    const { student: stu, periods, exams, subjects, generalAverages, remarks = [] } = data;
    const TD = 'border:1px solid #666;padding:4px 6px;';
    const AVG = 'background:#dbe7f5;font-weight:bold;';   // period average pops out
    const GEN = 'background:#c9dcf2;font-weight:bold;';   // general column pops more

    // Column layout: for each period → its exams then the period average.
    // A DIRECT period (one hidden mark) is a single column, like the cards.
    const periodCols = periods.map(p => {
      const pExams = exams.filter(e => e.PeriodID === p.PeriodID);
      return { period: p, exams: pExams, direct: pExams.length === 1 && !!pExams[0].IsDirect };
    });

    const headerRow1 = periodCols.map(pc =>
      `<td style="${TD}text-align:center;font-weight:bold;background:#f0f0f0;" colspan="${pc.direct ? 1 : pc.exams.length + 1}">${pc.period.PeriodName} (${pc.period.WeightPct}%)</td>`).join('');
    const headerRow2 = periodCols.map(pc =>
      pc.direct
        ? `<td style="${TD}text-align:center;${AVG}">Note</td>`
        : pc.exams.map(e => `<td style="${TD}text-align:center;background:#fafafa;">${e.ExamName}<br/><span style="color:#888;font-size:9px;">${e.WeightPct}%</span></td>`).join('')
          + `<td style="${TD}text-align:center;${AVG}">Moyenne</td>`).join('');

    const scoreCells = (s, extra = '') => periodCols.map(pc =>
      (pc.direct ? '' : pc.exams.map(e => {
        const v = s.perExam[e.ExamID];
        return `<td style="${TD}text-align:center;${extra}${v !== undefined && v < s.MaxGrade / 2 ? 'color:#c00;' : ''}">${v ?? (s.exemptExams?.[e.ExamID] ? 'D' : '—')}</td>`;
      }).join(''))
      + (() => {
        const v = s.perPeriod[pc.period.PeriodID];
        return `<td style="${TD}text-align:center;${AVG}${extra}${v !== null && v < s.MaxGrade / 2 ? 'color:#c00;' : ''}">${v ?? (s.perPeriodExempt?.[pc.period.PeriodID] ? 'D' : '—')}</td>`;
      })()
    ).join('');

    const subjectRows = subjects.map(s => `
      <tr>
        <td style="${TD}${(s.domains || []).length ? 'font-weight:bold;' : ''}">${s.SubjectNameFrench || s.SubjectName} <span style="color:#888;font-size:9px;">/${s.MaxGrade}</span></td>
        <td style="${TD}text-align:center;">${s.Coefficient}</td>
        ${scoreCells(s)}
        <td style="${TD}text-align:center;${GEN}${s.general !== null && s.general < s.MaxGrade / 2 ? 'color:#c00;' : ''}">${s.general ?? (s.generalExempt ? 'D' : '—')}</td>
      </tr>
      ${(s.domains || []).map(d => `
      <tr style="font-size:9px;color:#444;">
        <td style="${TD}padding-left:18px;">${d.SubjectNameFrench || d.SubjectName} <span style="color:#999;">/${d.MaxGrade}</span></td>
        <td style="${TD}"></td>
        ${scoreCells(d, 'font-size:9px;')}
        <td style="${TD}text-align:center;${GEN}font-size:9px;">${d.general ?? (d.generalExempt ? 'D' : '—')}</td>
      </tr>`).join('')}`).join('');

    const competencyTables = subjects.flatMap(s => [s, ...(s.domains || [])])
      .filter(s => Object.keys(s.competencies || {}).length > 0)
      .map(s => {
        const rows = new Map();
        for (const p of periods) {
          for (const c of (s.competencies[p.PeriodID] || [])) {
            if (!rows.has(c.text)) rows.set(c.text, {});
            rows.get(c.text)[p.PeriodID] = c.level;
          }
        }
        return `
          <h3 style="margin:12px 0 4px 0;font-size:13px;">${s.SubjectNameFrench || s.SubjectName} — Compétences</h3>
          <table style="width:100%;border-collapse:collapse;font-size:10px;">
            <tr style="background:#f0f0f0;font-weight:bold;">
              <td style="${TD}">Compétence</td>
              ${periods.map(p => `<td style="${TD}text-align:center;">${p.PeriodName}</td>`).join('')}
            </tr>
            ${[...rows.entries()].map(([text, lv]) => `
              <tr><td style="${TD}">${text}</td>
                ${periods.map(p => `<td style="${TD}text-align:center;">${lv[p.PeriodID] || '—'}</td>`).join('')}
              </tr>`).join('')}
          </table>`;
      }).join('');

    // Appréciations générales + décision de fin d'année
    const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br/>');
    const finalRow = remarks.find(r => !r.PeriodID);
    const remarkBlocks = periods
      .map(p => ({ p, r: remarks.find(x => x.PeriodID === p.PeriodID) }))
      .filter(x => x.r?.Remark)
      .map(x => `<p style="margin:3px 0;"><b>${x.p.PeriodName}:</b> ${esc(x.r.Remark)}</p>`)
      .join('');
    const remarksHTML = (remarkBlocks || finalRow) ? `
      <div style="margin-top:14px;border:1px solid #666;padding:8px;font-size:11px;">
        <p style="margin:0 0 4px 0;font-weight:bold;">Appréciations générales</p>
        ${remarkBlocks}
        ${finalRow?.Remark ? `<p style="margin:3px 0;"><b>Fin d'année:</b> ${esc(finalRow.Remark)}</p>` : ''}
        ${finalRow?.Decision ? `<p style="margin:6px 0 0 0;"><b>Décision de fin d'année:</b> ${esc(finalRow.Decision)}</p>` : ''}
      </div>` : '';

    return `
      <div style="text-align:center;margin-bottom:10px;">
        <h1 style="font-size:17px;margin:0;">Collège des Apôtres — Jounieh</h1>
        <h2 style="font-size:13px;margin:3px 0;color:#333;">Bulletin de notes · ${year}</h2>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:11px;margin-bottom:8px;">
        <tr>
          <td style="${TD}"><b>Élève:</b> ${stu.FirstName} ${stu.LastName}</td>
          <td style="${TD}"><b>Classe:</b> ${stu.Class} ${stu.Section || ''}</td>
          <td style="${TD}"><b>ID:</b> ${stu.StudentID}</td>
        </tr>
      </table>
      <table style="width:100%;border-collapse:collapse;font-size:10px;">
        <tr>
          <td style="${TD}background:#f0f0f0;font-weight:bold;" rowspan="2">Matière</td>
          <td style="${TD}background:#f0f0f0;font-weight:bold;text-align:center;" rowspan="2">Coef</td>
          ${headerRow1}
          <td style="${TD}text-align:center;${GEN}" rowspan="2">Général</td>
        </tr>
        <tr>${headerRow2}</tr>
        ${subjectRows}
        <tr>
          <td style="${TD}font-weight:bold;background:#eef5ff;" colspan="2">Moyenne générale /20</td>
          ${periodCols.map(pc =>
            `${pc.direct ? '' : `<td style="${TD}" colspan="${pc.exams.length}"></td>`}
             <td style="${TD}text-align:center;${AVG}">${generalAverages[pc.period.PeriodID] ?? '—'}</td>`).join('')}
          <td style="${TD}text-align:center;${GEN}">${generalAverages.overall ?? '—'}</td>
        </tr>
      </table>
      ${competencyTables}
      ${remarksHTML}
      <div style="display:flex;justify-content:space-between;margin-top:30px;font-size:11px;">
        <span>Le/La titulaire: ................</span>
        <span>La direction: ................</span>
        <span>Les parents: ................</span>
      </div>`;
  };

  const handlePrint = () => {
    const w = window.open('', '', 'width=950,height=700');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Bulletin - ${student.FirstName} ${student.LastName}</title>
      <style>@page{size:A4 landscape;margin:1.2cm}*{margin:0;padding:0;box-sizing:border-box}
      body{font-family:Arial,sans-serif;color:#111;padding:4px}</style>
      </head><body>${buildHTML()}</body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Grades — {student.FirstName} {student.LastName} · {year}</h3>
          <div className="flex gap-2">
            <button onClick={handlePrint} disabled={!data}
              className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
              <Printer size={15} /> Print
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {!data ? (
            <p className="text-center text-gray-500 py-8">Building grades report…</p>
          ) : (
            <div className="border-2 border-gray-300 rounded-lg p-5 bg-white shadow-inner overflow-x-auto"
                 dangerouslySetInnerHTML={{ __html: buildHTML() }} />
          )}
        </div>
      </div>
    </div>
  );
};

export default GradesReportModal;
